import type { GetServerSideProps, GetServerSidePropsContext, NextPage } from 'next';
import { getStringValue } from '@/clientOnlyUtils/clientUtils';
import { createTRPCClient, httpLink } from '@trpc/client';
import superjson from 'superjson';

import type { AppRouter } from '@poseidon/api/types';

type DocumentSlide = {
  id: string;
  order: number;
  url: string;
  webp: string | null;
  webpMobile: string | null;
  width: number;
  height: number;
  sizes: { w: number; h: number; url: string }[] | null;
};

type DocumentPublic = {
  title: string;
  description: string | null;
  primaryColor: string;
  secondaryColor: string;
  ogImageUrl: string | null;
  fileUrl: string;
  fileContentType: string;
  canBeIFramed: boolean | null;
  documentRepresentation: DocumentSlide[];
  url: string;
};

type PublicUserTeam = {
  gtmId: string | null;
  teamLogo: string | null;
  docUrl: string;
  teamName: string;
  email: string | null;
  name: string | null;
  title: string | null;
  profilePicture: string | null;
  contactNumber: string | null;
  calendarLink: string | null;
  calendarText: string | null;
  hubspot_owner_id: string | null;
};

import { PublicDocument } from '~/components/PublicDocument/PublicDocument';

function getApiServerUrl() {
  // eslint-disable-next-line no-restricted-properties
  return process.env.NEXT_PUBLIC_BACKEND_URL ?? 'http://localhost:3001';
}

const client = createTRPCClient<AppRouter>({
  links: [
    httpLink({
      url: `${getApiServerUrl()}/api/trpc`,
      transformer: superjson,
    }),
  ],
});

interface DocumentPageProps {
  initialSharingData?: { sharing: DocumentPublic; userTeam: PublicUserTeam };
}

const Document: NextPage<DocumentPageProps> = (props) => {
  const initialSharingData = props.initialSharingData;
  const host_ssr = (props as unknown as { host?: string | undefined }).host;

  return <PublicDocument initialSharingData={initialSharingData} host={host_ssr} />;
};

export const getServerSideProps = (async (context: GetServerSidePropsContext) => {
  const docIdRaw = getStringValue(context.params?.docId);
  const docId = docIdRaw != null ? docIdRaw.split('+')[0] : undefined;

  if (docId == null) return { notFound: true };

  try {
    const data = await client.sharingPublic.idOrNull.query(docId);

    if (data == null) {
      return { notFound: true };
    }

    return {
      props: {
        id: docId,
        initialSharingData: data,
        host: context.req.headers.host,
      },
    };
  } catch (error) {
    console.error('Error fetching sharing data:', error);
    return { notFound: true };
  }
}) satisfies GetServerSideProps<DocumentPageProps>;

export default Document;
